import React, { Fragment, useCallback, useEffect, useState } from "react";

import axios from "axios";
import useRequest from "../../hooks/useRequest";
import { config } from "../../components/constant";
import { useContextData } from "../../store/ContextData";
import { commonFunction } from "../../components/common-functions";

import Head from "../../components/head";
import Header from "../../components/dashboard/header";
import SideNav from "../../components/dashboard/side-navbar";
import ApprovalBlock from "../../components/dashboard/approval-block";
import ModalWithInput from "../../components/dashboard/modal-with-input";
import DeleteConfirmModal from "../../components/dashboard/delete-confirm";

import refresh from "../../static/images/refresh.png";
import loadingIcon from "../../static/svg/ring-alt.svg";

const Approvals = () => {
  const { userId, nanobar } = useContextData();

  const { showAlert } = commonFunction;
  const [loading, setLoading] = useState(true);
  const [decisionType, setDecisionType] = useState("");
  const [rejectingReason, setRejectingReason] = useState("");
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  const {
    data: pendingApprovalData, loading: pendingApprovalLoading, callAPI: callPendingAPI
  } = useRequest("api/v1/document/approvals/pending");
  useEffect(() => {
    if(!pendingApprovalLoading && pendingApprovalData){
      const { approvals } = pendingApprovalData;
      setPendingApprovals(approvals);
      setLoading(false);
    }
  }, [pendingApprovalLoading, pendingApprovalData]);

  const refreshPendingApprovals = useCallback(() => {
    setPendingApprovals([]);
    setLoading(true);
    callPendingAPI();
  }, [callPendingAPI]);

  const {
    data: deleteData, loading: deleteLoading, callAPI: callDeleteAPI
  } = useRequest(`api/v1/document/approvals/${selectedApproval}`, false, {}, "DELETE");
  useEffect(() => {
    if(!deleteLoading && deleteData){
      setSelectedApproval(null);
      setDecisionLoading(false);
      setShowDeleteModal(false);
      refreshPendingApprovals();
    }
  }, [deleteLoading, deleteData, refreshPendingApprovals]);

  const callDecisionAPI = useCallback(() => {
    axios.post(
      `${config.API_URL}api/v1/document/approvals/${selectedApproval}/${decisionType}`, 
      (decisionType === "reject" ? {reason: rejectingReason} : {})
    ).then(() => {
      setSelectedApproval(null);
      setDecisionType("");
      setRejectingReason("");
      refreshPendingApprovals();
      if(decisionType === "approve"){
        showAlert("Document approved successfully!", "success");
        setShowApprovalModal(false);
        return;
      }
      showAlert("Document rejected successfully!", "error");
      setShowRejectionModal(false);
    }).catch(error => {
      showAlert(error.response.data.message, "error");
    });
  }, [selectedApproval, rejectingReason, decisionType, refreshPendingApprovals, showAlert]);

  const handleDecision = (decision, approval) => {
    setDecisionType(decision);
    setSelectedApproval(approval._id);
    if(decision === "approve"){
      setShowApprovalModal(true);
      return;
    }
    setShowRejectionModal(true);
  };

  return (
    <Fragment>
      <Head customTitle={"Approvals"} />
      <Header></Header>
      <SideNav name={"approvals"}/>
      <main className="dashboard-container approvals">
        <div className="main-container">
          <div className="flex-container">
            <div className="form-group flexbox two-third left">
              <div className="searchbar">
                <div className="custom-search-bar relative">
                  <span className="search-icon"/>
                  <input type="text" name="search-text" 
                    className="input-field" placeholder="Search approvals..." 
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="approval-list flex-container no-margin column with-gap">
                {(loading || !userId) &&
                  <div className="loading-container loading">
                    <img src={loadingIcon} className="loading" alt="loading"/>
                    <span id="loading" className="text-left">Loading...</span>
                  </div>
                }
                {!loading && userId && pendingApprovals.length === 0 &&
                  <div className="no-data">
                    No pending approvals found.
                  </div>
                }
                {!loading && userId  && pendingApprovals.length > 0 &&
                  pendingApprovals.map((approval) => (
                    <ApprovalBlock
                      nanobar={nanobar}
                      key={approval._id}
                      approval={approval}
                      userId={userId}
                      onDecision={handleDecision}
                      onDelete={(approvalId) => {
                        setSelectedApproval(approvalId);
                        setShowDeleteModal(true);
                      }}
                    />
                  ))
                }
              </div>
            </div>
            <div className="flexbox one-third right">
              <div
                className={`btn-link ${loading ? "disabled" : ""}`}
                onClick={refreshPendingApprovals}
              >
                <img src={refresh} className="refresh icon" alt="icon" />
                Fetch latest approvals
              </div>
            </div>
          </div>
        </div>
        {showApprovalModal && (
          <DeleteConfirmModal
            disableStatus={decisionLoading}
            desc="Are you sure you want to approve this generated document?"
            buttonText="Approve"
            isLoading={decisionLoading}
            disable={setDecisionLoading}
            close={() => {
              setDecisionLoading(false);
              setShowApprovalModal(false);
              setSelectedApproval(null);
              setDecisionType("");
            }}
            doDelete={() => {
              setTimeout(() => {
                callDecisionAPI();
              });
            }}
          />
        )}
        {showDeleteModal && (
          <DeleteConfirmModal
            disableStatus={deleteLoading}
            desc="Are you sure you want to delete this approval?"
            buttonText="Delete"
            isLoading={deleteLoading}
            disable={setDecisionLoading}
            close={() => {
              setShowDeleteModal(false);
              setSelectedApproval(null);
            }}
            doDelete={() => {
              setTimeout(() => {
                callDeleteAPI();
              });
            }}
          />
        )}
        {showRejectionModal && (
          <ModalWithInput
            close={() => {
              setDecisionLoading(false);
              setShowRejectionModal(false);
              setSelectedApproval(null);
              setDecisionType("");
            }}
            buttonText="Reject"
            disableStatus={decisionLoading}
            isLoading={decisionLoading}
            disable={setDecisionLoading}
            inputLabel="Reason for rejection"
            required={true}
            inputPlaceholder="Write down the reason for rejection"
            desc="Are you sure you want to reject this generated document?"
            doAction={() => {
              setTimeout(() => {
                callDecisionAPI();
              });
            }}
            inputData={rejectingReason}
            setInputData={setRejectingReason}
          />
        )}
      </main>
    </Fragment>
  );
};

export default Approvals;
